import {
  CreateUserRequest,
  ExclusionUpdate,
  SalesExpert,
  UpdateUserRequest,
  UserManagement
} from '@/types/user-management.types';
import { formatToLocalDateTimeShort } from '@/utils/dateFormatter';
import { UserFormData } from './components';

export const headerColumns = [
  { key: 'email', label: 'Електронна пошта', className: 'um-col-email' },
  { key: 'display_name', label: "Ім'я", className: 'um-col-name' },
  { key: 'role', label: 'Роль', className: 'um-col-role' },
  { key: 'phone_number', label: 'Телефон', className: 'um-col-phone' },
  { key: 'is_active', label: 'Статус', className: 'um-col-status' },
  { key: 'notify', label: 'Сповіщення', className: 'um-col-notifications' },
  { key: 'created_at', label: 'Створено', className: 'um-col-created' },
  { key: '', label: 'Дії', className: 'um-col-actions' }
];

export const marketersTableHeaderColumns = [
  { key: 'expert_email', label: 'Пошта маркетолога', className: 'um-col-expert-email' },
  { key: 'expert_name', label: "Ім'я", className: 'um-col-expert-name' },
  { key: 'expert_phone', label: 'Телефон', className: 'um-col-expert-phone' }
];

export const roleLabels: { [key: string]: string } = {
  Administrator: 'Адміністратор',
  ADMINISTRATOR: 'Адміністратор',
  administrator: 'Адміністратор',
  Manager: 'Менеджер',
  MANAGER: 'Менеджер',
  manager: 'Менеджер',
  Marketer: 'Маркетолог',
  MARKETER: 'Маркетолог',
  marketer: 'Маркетолог',
  User: 'Користувач',
  USER: 'Користувач',
  user: 'Користувач'
};

export const getFilteredUsers = (
  users: UserManagement[],
  searchQuery: string,
  sortOrder: 'ASC' | 'DESC' = 'ASC',
  sortBy?: string
): UserManagement[] => {
  const lowerQuery = (searchQuery || '').toLowerCase();

  let result = users;

  if (lowerQuery.trim()) {
    result = users.filter(
      user =>
        user.email.toLowerCase().includes(lowerQuery) ||
        user.display_name.toLowerCase().includes(lowerQuery) ||
        roleLabels[user.role].toLowerCase().includes(lowerQuery) ||
        (user.phone_number && user.phone_number.includes(lowerQuery)) ||
        (user.is_active ? 'активний' : 'неактивний').includes(lowerQuery) ||
        (user.notify ? 'увімкнено' : 'вимкнено').includes(lowerQuery) ||
        formatToLocalDateTimeShort(user.created_at).includes(lowerQuery)
    );
  }

  if (!sortBy) return result;

  const sorted = [...result].sort((a: any, b: any) => {
    let aValue: any = (a as any)[sortBy];
    let bValue: any = (b as any)[sortBy];

    // Handle null/undefined values
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return sortOrder === 'ASC' ? 1 : -1;
    if (bValue == null) return sortOrder === 'ASC' ? -1 : 1;

    // For role column, sort by transformed Cyrillic values
    if (sortBy === 'role') {
      aValue = roleLabels[aValue];
      bValue = roleLabels[bValue];
    }

    if (sortBy === 'created_at') {
      aValue = new Date(aValue).getTime();
      bValue = new Date(bValue).getTime();
      return sortOrder === 'ASC' ? aValue - bValue : bValue - aValue;
    } else if (typeof aValue === 'string') {
      const resultCompare = aValue.localeCompare(bValue, 'uk', { sensitivity: 'base' });
      return sortOrder === 'ASC' ? resultCompare : -resultCompare;
    }

    if (aValue < bValue) return sortOrder === 'ASC' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'ASC' ? 1 : -1;
    return 0;
  });

  return sorted;
};

export const getFilteredExperts = (experts: SalesExpert[], searchQuery: string) => {
  const lowerQuery = searchQuery.toLowerCase();
  if (!lowerQuery.trim()) {
    return experts;
  }

  return experts.filter(
    expert => 
      (expert.expert_email?.toLowerCase() || '').includes(lowerQuery) || 
      (expert.expert_phone || '').includes(lowerQuery) ||
      `${expert.expert_name || ''} ${expert.expert_surname || ''}`.toLowerCase().includes(lowerQuery)
  );
};

export const getUpdatedExperts = (
  experts: SalesExpert[],
  expertEmail: string,
  stationUnicode: number,
  active: boolean
): SalesExpert[] => {
  return experts.map(expert =>
    expert.expert_email === expertEmail
      ? {
          ...expert,
          stations: expert.stations.map(station =>
            station.unicode === stationUnicode ? { ...station, active } : station
          )
        }
      : expert
  );
};

export const getActualChanges = (updatedExperts: SalesExpert[], originalExperts: SalesExpert[]): ExclusionUpdate[] => {
  const actualChanges: ExclusionUpdate[] = [];

  updatedExperts.forEach(expert => {
    const originalExpert = originalExperts.find(orig => orig.expert_email === expert.expert_email);

    if (!originalExpert) return;

    expert.stations.forEach(station => {
      const originalStation = originalExpert.stations.find(orig => orig.unicode === station.unicode);

      if (originalStation && originalStation.active !== station.active) {
        actualChanges.push({
          expert_email: expert.expert_email,
          expert_phone: expert.expert_phone,
          excluded_station_unicode: Number(station.unicode),
          active: station.active
        });
      }
    });
  });

  return actualChanges;
};

export const getCreateOrUpdateErrorMessage = (err: any, editingUser: UserManagement | null): string => {
  if (err?.data?.message) return err.data.message;
  if (err?.status === 409) {
    return 'Користувач з такою електронною поштою вже існує';
  }
  if (err?.status === 403) {
    return 'Немає прав для виконання цієї операції';
  }
  return editingUser ? 'Не вдалося оновити користувача' : 'Не вдалося створити користувача';
};

export const getDeleteUserErrorMesage = (err: any): string => {
  if (err?.data?.message) return err.data.message;
  if (err?.status === 403) {
    return 'Немає прав для виконання цієї операції';
  }
  return 'Не вдалося видалити користувача';
};

export const getUpdateData = (data: UserFormData): UpdateUserRequest => {
  return {
    display_name: data.display_name,
    role: data.role,
    is_active: data.is_active,
    notify: data.notify,
    phone_number: data.phone_number ?? undefined
  };
};

export const getCreateData = (data: UserFormData): CreateUserRequest => {
  return {
    display_name: data.display_name,
    email: data.email,
    role: data.role,
    notify: data.notify,
    phone_number: data.phone_number || undefined
  };
};
